import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const STATUS_STYLE = {
  pending: 'bg-yellow-50 text-yellow-800',
  removed: 'bg-red-50 text-red-700',
  dismissed: 'bg-gray-100 text-gray-600',
};

function fmt(dt) { return dt ? new Date(dt).toLocaleString() : '\u2014'; }

function ReportRow({ r, onAction, onReinstate, busyId }) {
  return (
    <tr data-testid="content-report-row" className="border-t border-brand-cream-dark align-top">
      <td className="py-2 pr-4 whitespace-nowrap text-brand-green/80">{fmt(r.created_at)}</td>
      <td className="py-2 pr-4 max-w-xs">
        <a href={r.content_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-green underline break-all">
          {r.content_type} <ExternalLink className="w-3 h-3" />
        </a>
        <div className="text-xs text-brand-green/60 mt-1">{r.listing_address || r.listing_id || 'No listing linked'}</div>
      </td>
      <td className="py-2 pr-4 text-xs text-brand-green/80 max-w-xs">
        <div className="font-medium text-brand-green">{r.reporter_name}</div>
        <div>{r.reporter_email}</div>
        <div className="mt-1 text-brand-green/60 line-clamp-3" title={r.copyrighted_work}>{r.copyrighted_work}</div>
      </td>
      <td className="py-2 pr-4 text-xs text-brand-green/80">
        {r.owner_email || '\u2014'}
        {r.owner_email && <div data-testid="content-report-strikes">Strikes: {r.owner_strikes}/3{r.owner_suspended ? ' · suspended' : ''}</div>}
        {r.owner_suspended && (
          <button data-testid="content-report-reinstate-btn" onClick={() => onReinstate(r)} className="mt-1 text-xs underline text-brand-green hover:text-brand-green-dark">
            Reinstate account
          </button>
        )}
      </td>
      <td className="py-2 pr-4"><span className={`px-2 py-0.5 rounded-full text-xs capitalize ${STATUS_STYLE[r.status]}`}>{r.status}</span></td>
      <td className="py-2 pr-4 whitespace-nowrap">
        {r.status === 'pending' ? (
          <div className="flex gap-2">
            <button data-testid="content-report-remove-btn" disabled={busyId === r.id} onClick={() => onAction(r, 'remove')}
              className="px-3 py-1.5 rounded-lg text-xs bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">Remove + strike</button>
            <button data-testid="content-report-dismiss-btn" disabled={busyId === r.id} onClick={() => onAction(r, 'dismiss')}
              className="px-3 py-1.5 rounded-lg text-xs border border-brand-cream-dark text-brand-green hover:bg-brand-cream disabled:opacity-60">Dismiss</button>
          </div>
        ) : <span className="text-xs text-brand-green/60">{r.resolved_by || ''}</span>}
      </td>
    </tr>
  );
}

export function ContentReportsPanel() {
  const { authAxios } = useAuth();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get('/admin/content-reports', { params: { status: status || undefined } });
      setRows(data);
    } catch (err) {
      toast({ title: 'Could not load reports', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [authAxios, status, toast]);
  useEffect(() => { load(); }, [load]);

  const onAction = async (r, action) => {
    if (action === 'remove' && !window.confirm('Remove this media and add a strike to the listing owner?')) return;
    setBusyId(r.id);
    try {
      const { data } = await authAxios.post(`/admin/content-reports/${r.id}/resolve`, { action });
      toast({ title: action === 'remove' ? 'Content removed' : 'Report dismissed',
        description: data.suspended ? 'Owner reached 3 strikes and was suspended.' : (data.strikes ? `Owner strikes: ${data.strikes}/3` : undefined) });
      load();
    } catch (err) {
      toast({ title: 'Action failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const onReinstate = async (r) => {
    if (!window.confirm(`Reinstate ${r.owner_email}? Their strikes stay on record.`)) return;
    try {
      await authAxios.post(`/admin/users/${r.listing_owner_id}/reinstate`);
      toast({ title: 'Account reinstated', description: r.owner_email });
      load();
    } catch (err) {
      toast({ title: 'Reinstate failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  return (
    <div data-testid="admin-content-reports-panel" className="bg-white rounded-2xl border border-brand-cream-dark p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-serif text-2xl text-brand-green">DMCA content reports</h2>
          <p className="text-sm text-brand-green/70 mt-1">Report &rarr; review &rarr; removal. 3 upheld reports suspend the owner&apos;s account.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="content-reports-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 rounded-lg border border-brand-cream-dark px-2 text-sm text-brand-green">
            <option value="pending">Pending</option><option value="removed">Removed</option>
            <option value="dismissed">Dismissed</option><option value="">All</option>
          </select>
          <button onClick={load} data-testid="content-reports-refresh" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-brand-cream-dark text-brand-green hover:bg-brand-cream">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>
      {rows.length === 0 && !loading ? (
        <p data-testid="content-reports-empty" className="text-sm text-brand-green/60 py-6 text-center">No reports here.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-brand-green/60">
                {['Received', 'Content', 'Reporter', 'Listing owner', 'Status', 'Action'].map((h) => <th key={h} className="py-2 pr-4">{h}</th>)}
              </tr>
            </thead>
            <tbody>{rows.map((r) => <ReportRow key={r.id} r={r} onAction={onAction} onReinstate={onReinstate} busyId={busyId} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
